const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export interface WiseProfile {
  id: number;
  type: string;
  fullName: string;
}

export interface WiseBalanceAmount {
  value: number;
  currency: string;
}

export interface WiseBalance {
  id: number;
  currency: string;
  amount: WiseBalanceAmount;
  reservedAmount: WiseBalanceAmount;
  type: string;
  name: string;
  icon: { type: string; value: string } | null;
  investmentState: string | null;
  creationTime: string;
  modificationTime: string;
  visible: boolean;
}

export interface WiseStatementTransaction {
  type: string;
  date: string;
  amount: { value: number; currency: string };
  totalFees: { value: number; currency: string };
  details: {
    type: string;
    description: string;
    senderName?: string;
    recipientName?: string;
    paymentReference?: string;
  };
  exchangeDetails: {
    toAmount?: { value: number; currency: string };
    fromAmount?: { value: number; currency: string };
    rate?: number;
  } | null;
  runningBalance: { value: number; currency: string };
  referenceNumber: string;
}

export interface WiseStatement {
  accountHolder: { type: string; fullName: string };
  issuer: { name: string; firstLine: string; city: string; postCode: string; stateCode: string; country: string };
  bankDetails: any | null;
  transactions: WiseStatementTransaction[];
  startOfStatementBalance: { value: number; currency: string };
  endOfStatementBalance: { value: number; currency: string };
  query: { intervalStart: string; intervalEnd: string; currency: string; type: string };
}

async function callWiseFunction(body: Record<string, unknown>) {
  const apiUrl = `${SUPABASE_URL}/functions/v1/wise-balances`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `Wise API error: ${response.status}`);
  }

  return response.json();
}

export async function fetchWiseProfiles(wiseApiKey: string): Promise<WiseProfile[]> {
  return callWiseFunction({ wiseApiKey, action: 'fetchProfiles' });
}

export async function fetchWiseBalances(
  wiseApiKey: string,
  profileId: number
): Promise<WiseBalance[]> {
  return callWiseFunction({ wiseApiKey, action: 'fetchBalances', profileId });
}

export async function fetchWiseStatement(
  wiseApiKey: string,
  profileId: number,
  balanceId: number,
  currency: string,
  startDate: string,
  endDate: string
): Promise<WiseStatement> {
  return callWiseFunction({
    wiseApiKey,
    action: 'fetchStatement',
    profileId,
    balanceId,
    currency,
    startDate,
    endDate,
  });
}
